/**
 * One AssignmentBlade row -> one POST /deployments/{id}/assignments entry.
 *
 * WHY THIS EXISTS: the blade is the editor for an assignment everywhere it is edited, but until
 * now only the DEPLOYMENT TEMPLATE wizard used it — the Assign action on the deployments list had
 * its own cut-down form offering intent and a target and nothing else. So the same object got a
 * rich editor on one screen and a bare one on another, and notifications, availability, deadline,
 * restart grace, filters and delivery optimization were simply unreachable when assigning an app
 * that was already deployed. The API accepted all of them the whole time
 * (api/deployments.py, AssignmentSettings).
 *
 * This mirrors the BACKEND's own translation for the template path,
 * `intune_template_service.assignment_call_args` (services/intune_template_service.py:597), so
 * both routes to Intune agree. Where that function and this one could drift, the parity test
 * pins them.
 *
 * ⚠️ snake_case is deliberate, not sloppiness. `intune_publisher._build_assignment_settings`
 * reads snake_case; camelCase is not a type error and not a Graph error — it is silently
 * DROPPED. That is exactly how bulk publish once lost its schedule.
 */
import type { AssignmentRow } from '../components/AssignmentBlade'
import type { Assignment, AssignmentCreate } from '../services/api'

/**
 * Returns null when the row cannot be assigned — a group row with no group chosen. Graph accepts
 * a target with a null groupId and then assigns the app to nobody, so this is reported to the
 * caller rather than sent.
 */
export function rowToAssignmentCreate(row: AssignmentRow): AssignmentCreate | null {
  // `rawIntent` wins only while it still agrees with the edited intent: the blade cannot select
  // availableWithoutEnrollment, so a row that came back as one and was NOT retargeted keeps it,
  // while a row the operator moved to Required or Uninstall takes the new value.
  const edited = (row.intent || 'required') as AssignmentCreate['intent']
  const intent = (
    row.rawIntent === 'availableWithoutEnrollment' && edited === 'available'
      ? 'availableWithoutEnrollment'
      : edited
  ) as AssignmentCreate['intent']
  const include = (row.mode ?? 'include') !== 'exclude'

  const target: AssignmentCreate['target'] = { type: row.target as any, include }
  if (row.target === 'group') {
    if (!row.groupId) return null
    target.id = row.groupId
  }
  // Intune takes no assignment filter and no win32LobAppAssignmentSettings on an exclusion: an
  // excluded group is excluded whole, and Graph refuses the assignment if either is present.
  if (!include) return { intent, target }

  if (row.filter) {
    target.filter_id = row.filter
    target.filter_type = row.filterMode || 'include'
  }

  const settings: any = {}
  if (row.notifications) settings.notifications = row.notifications
  if (row.contentDownload) {
    // Graph's vocabulary, not ours: foreground == "not throttled".
    settings.delivery_optimization =
      row.contentDownload === 'foreground' ? 'foreground' : 'notConfigured'
  }

  const its: any = { use_local_time: row.timeZone === 'device' }
  if (row.availability && row.availability !== 'asap') its.start_datetime = row.availability
  // Required-only, as the backend enforces: Intune ignores a deadline on the other intents.
  if (row.deadline && row.deadline !== 'asap' && intent === 'required') {
    its.deadline_datetime = row.deadline
  }
  settings.install_time_settings = its

  const rgp = row.restartGracePeriod
  if (rgp?.enabled) {
    const rs: any = {
      grace_period_minutes: rgp.graceMinutes ?? 1440,
      countdown_minutes: rgp.countdownMinutes ?? 15,
    }
    // allowSnooze has no Graph boolean: snooze is DISABLED by omitting the duration.
    if (rgp.allowSnooze ?? true) rs.snooze_minutes = rgp.snoozeMinutes ?? 240
    settings.restart_settings = rs
  }

  // No control edits this — it rides back exactly as it was read. Saving replaces the whole
  // assignment set, so omitting it turns Company Portal auto-update off for a superseded app
  // without anyone choosing to.
  if (row.autoUpdateSupersededApps) settings.auto_update_superseded_apps = true

  return { intent, target, settings }
}

export interface MappedAssignments {
  assignments: AssignmentCreate[]
  /** Rows dropped for want of a group, so the caller can say so instead of silently sending fewer. */
  skipped: number
}

export function rowsToAssignments(rows: AssignmentRow[]): MappedAssignments {
  const assignments: AssignmentCreate[] = []
  let skipped = 0
  for (const r of rows) {
    const a = rowToAssignmentCreate(r)
    if (a) assignments.push(a)
    else skipped += 1
  }
  return { assignments, skipped }
}

/**
 * A LIVE assignment, read back from Intune, as a blade row — the inverse of
 * `rowToAssignmentCreate`.
 *
 * WHY: the Assign screen is an editor for the app's COMPLETE assignment set, not a form that adds
 * another one. Opening it empty meant an administrator could only ever append, and saving replaced
 * a set they were never shown — the reference seeds its blade from the deployment's current
 * assignments and so must this.
 *
 * ⚠️ The list endpoint reads Graph v1.0, which does not return assignment FILTER properties, and
 * `get_app_assignments` does not parse them either. A filtered assignment therefore round-trips
 * through this screen with its filter dropped. Until the read side moves to beta, saving an
 * unedited row can widen it — which is why the caller warns rather than silently saving.
 */
export function assignmentToRow(a: Assignment): AssignmentRow {
  const t: any = a.target ?? {}
  const s: any = a.settings ?? {}
  // Graph gives these back in ITS OWN camelCase (they are the raw objects), not the snake_case
  // this module sends.
  const its: any = s.install_time_settings ?? {}
  const rs: any = s.restart_settings ?? {}
  // The blade has no availableWithoutEnrollment section; it EDITS as Available so the row is
  // visible at all. `rawIntent` keeps the true value so saving an untouched row does not quietly
  // downgrade the assignment — saving replaces the whole set, so a lost intent is a changed one.
  const intent = (a.intent === 'availableWithoutEnrollment' ? 'available' : a.intent) as
    AssignmentRow['intent']

  return {
    intent,
    rawIntent: a.intent,
    autoUpdateSupersededApps: !!(s as any).auto_update_superseded_apps,
    target: (t.type === 'allUsers' || t.type === 'allDevices' ? t.type : 'group') as
      AssignmentRow['target'],
    groupId: t.id ?? null,
    // A live assignment names a group that already exists, whatever created it.
    groupSource: 'existing',
    mode: t.include === false ? 'exclude' : 'include',
    filter: t.filter_id ?? null,
    filterMode: (t.filter_type ?? 'include') as AssignmentRow['filterMode'],
    notifications: (s.notifications ?? 'showAll') as AssignmentRow['notifications'],
    timeZone: its.useLocalTime ? 'device' : 'utc',
    availability: its.startDateTime ?? 'asap',
    deadline: its.deadlineDateTime ?? (intent === 'required' ? 'asap' : null),
    restartGracePeriod: {
      enabled: !!s.restart_settings,
      graceMinutes: rs.gracePeriodInMinutes ?? 1440,
      countdownMinutes: rs.countdownDisplayBeforeRestartInMinutes ?? 15,
      // Snooze has no Graph boolean: it is disabled by the duration being absent.
      allowSnooze: rs.restartNotificationSnoozeDurationInMinutes != null,
      snoozeMinutes: rs.restartNotificationSnoozeDurationInMinutes ?? 240,
    },
    contentDownload: s.delivery_optimization === 'foreground' ? 'foreground' : 'background',
  }
}

/** Every live assignment as blade rows, in the order Intune returned them. */
export function assignmentsToRows(assignments: Assignment[]): AssignmentRow[] {
  return (assignments ?? []).map(assignmentToRow)
}

/**
 * The API rejects an exclusion-only set with a 400 (api/deployments.py:1387), because excludes
 * without an include assign the app to nobody. Checked here so the wizard can say so before the
 * round trip rather than surfacing a raw error.
 */
export function hasIncludeTarget(assignments: AssignmentCreate[]): boolean {
  return assignments.some((a) => a.target.include)
}
